import { UcoastEl } from '@/scripts/core/UcoastEl'
import { type Hls } from '@/scripts/global'
import { TsDOM as q } from '@/scripts/core/TsDOM'
import { percentageSeen } from '@/scripts/theme/animations'
import { type ArtDirection } from '@/scripts/core/art-direction'
import { ambientMotionEnabled } from '@/scripts/core/motion'

function isVideoPlaying(video: HTMLVideoElement): boolean {
	return !video.paused && !video.ended
}

function selectHlsVariant(
	manifest: string,
	masterUrl: URL,
	maxHeight: number
): string | undefined {
	const lines = manifest.split(/\r?\n/).map((line) => line.trim())
	let selectedHeight = 0
	let selectedUrl: string | undefined

	for (let index = 0; index < lines.length; index += 1) {
		const height = Number(
			lines[index].match(/RESOLUTION=\d+x(\d+)/)?.[1] ?? 0
		)
		if (height <= selectedHeight || height > maxHeight) continue

		const source = lines.slice(index + 1).find((line) => line && !line.startsWith('#'))
		if (!source) continue
		selectedHeight = height
		selectedUrl = new URL(source, masterUrl).href
	}

	return selectedUrl
}

function shouldHoldAutoplayForUserIntent(autoplayOnMobile: boolean): boolean {
	const connection = (
		navigator as Navigator & {
			connection?: {
				downlink?: number
				effectiveType?: string
				saveData?: boolean
			}
		}
	).connection
	const constrainedConnection =
		connection?.saveData === true ||
		connection?.effectiveType === 'slow-2g' ||
		connection?.effectiveType === '2g' ||
		connection?.effectiveType === '3g' ||
		(connection?.downlink !== undefined && connection.downlink < 2)

	return (
		(!autoplayOnMobile && window.matchMedia('(max-width: 767px)').matches) ||
		constrainedConnection
	)
}

export class UcoastVideo extends UcoastEl {
	static htmlSelector = 'ucoast-video'
	videoEl: HTMLVideoElement
	hlsReady: boolean
	hlsSource?: string
	mp4Source?: string
	initialized = false
	usingMp4Source: boolean
	hlsInstance: Hls
	eventDriven: boolean
	eventEnabled: boolean
	autoplayDisabled: boolean
	limitHlsOnMobile: boolean
	nativeHlsSourcePromise?: Promise<string>
	wrapper?: ArtDirection
	toggleButton?: HTMLButtonElement
	toggleLabel?: HTMLElement
	muteToggleButton?: HTMLButtonElement
	muteToggleLabel?: HTMLElement
	controlsBound: boolean
	userPaused: boolean
	userPlayRequested: boolean
	autoplayReady: boolean
	autoplayReadyTimer?: number
	autoplayIntentBound: boolean
	onAutoplayIntentBound: EventListener
	retryAttempts: number
	retryInitTimer?: number
	onToggleClickBound: EventListener
	onVideoStateChangedBound: EventListener
	onMuteToggleClickBound: EventListener
	onVideoVolumeChangedBound: EventListener

	constructor() {
		super()
		this.videoEl = q.rs<HTMLVideoElement>('video', this)
		this.hlsSource = this.videoEl.getAttribute('data-hls-src') ?? undefined
		this.hlsReady = false
		this.mp4Source = this.videoEl.getAttribute('data-mp4-src') ?? undefined
		this.usingMp4Source = false
		this.initialized = true
		this.eventDriven = this.hasAttribute('data-event-driven')
		this.eventEnabled = false
		this.autoplayDisabled = this.hasAttribute('data-uc-autoplay-disabled')
		this.limitHlsOnMobile =
			this.hasAttribute('data-uc-limit-hls-on-mobile') &&
			window.matchMedia('(max-width: 989px)').matches
		this.nativeHlsSourcePromise = undefined
		this.wrapper = undefined
		this.toggleButton = q.os<HTMLButtonElement>(
			'[data-uc-video-toggle]',
			this
		)
		this.toggleLabel = q.os<HTMLElement>(
			'[data-uc-video-toggle-label]',
			this
		)
		this.muteToggleButton = q.os<HTMLButtonElement>(
			'[data-uc-video-mute-toggle]',
			this
		)
		this.muteToggleLabel = q.os<HTMLElement>(
			'[data-uc-video-mute-toggle-label]',
			this
		)
		this.controlsBound = false
		this.userPaused = false
		this.userPlayRequested = false
		this.autoplayReady = this.eventDriven
		this.autoplayReadyTimer = undefined
		this.autoplayIntentBound = false
		this.onAutoplayIntentBound = this.onAutoplayIntent.bind(this)
		this.retryAttempts = 0
		this.retryInitTimer = undefined
		this.onToggleClickBound = this.onToggleClick.bind(this)
		this.onVideoStateChangedBound = this.onVideoStateChanged.bind(this)
		this.onMuteToggleClickBound = this.onMuteToggleClick.bind(this)
		this.onVideoVolumeChangedBound = this.updateMuteToggleState.bind(this)
	}

	override async connectedCallback() {
		super.connectedCallback()
		this.onConnectedCallback()
		this.bindControls()
		if (!this.eventDriven && !this.autoplayDisabled) {
			this.scheduleAutoplayReady()
		}
	}

	onConnectedCallback() {
		const mediaManager = window?.Ucoast?.mediaManager
		if (!mediaManager) {
			this.markForRetry()
			this.scheduleInitRetry()
			return
		}
		this.retryAttempts = 0
		this.clearInitRetry()
		this.unMarkForRetry()
		if (this.hlsSource || this.mp4Source) {
			mediaManager.addVideo(this)
		}
	}

	markForRetry() {
		this.setAttribute('data-retry', 'true')
	}

	// used by mediaManager to retry initialization
	unMarkForRetry() {
		this.removeAttribute('data-retry')
	}

	override async disconnectedCallback() {
		super.disconnectedCallback()
		this.clearInitRetry()
		this.clearAutoplayReady()
		this.unbindAutoplayIntent()
		this.unbindControls()
		if (this.hlsInstance) {
			this.hlsInstance.destroy()
		}
		window?.Ucoast?.mediaManager?.removeVideo(this)
	}

	// both playEvent methods are used by remote components
	async playEventOn() {
		if (!this.eventDriven) return
		this.eventEnabled = true
		this.autoplayReady = true
		await this.play()
	}

	playEventOff() {
		this.eventDriven = true // sometimes we need to convert auto-play to event-driven
		this.eventEnabled = false
		this.userPlayRequested = false
		this.pause()
	}

	isTouchingViewport() {
		const rect = this.getBoundingClientRect()

		const isVisible =
			rect.top < window.innerHeight &&
			rect.bottom > 0 &&
			rect.left < window.innerWidth &&
			rect.right > 0

		return isVisible
	}

	async play(options: { userInitiated?: boolean } = {}) {
		// all conditions that should prevent the video from playing must go here
		if (options.userInitiated) this.userPlayRequested = true
		const userRequestedPlayback =
			options.userInitiated === true || this.userPlayRequested
		if (!this.isTouchingViewport()) return
		if (this.eventDriven && !this.eventEnabled) return
		if (!this.eventDriven && !this.autoplayReady && !userRequestedPlayback)
			return
		if (!userRequestedPlayback && !ambientMotionEnabled()) return
		if (!userRequestedPlayback && this.userPaused) return
		if (this.isHiddenForViewport()) return
		const mediaManager = window?.Ucoast?.mediaManager
		const canPlayNativeHls =
			this.videoEl.canPlayType('application/vnd.apple.mpegurl') !== ''
		if (
			this.limitHlsOnMobile &&
			this.hlsSource &&
			canPlayNativeHls &&
			!this.videoEl.src
		) {
			this.videoEl.src = await this.resolveNativeHlsSource()
		}
		if (this.hlsSource && !this.videoEl.src && !this.usingMp4Source) {
			if (!mediaManager && canPlayNativeHls) {
				this.videoEl.src = this.hlsSource
			} else if (mediaManager && !mediaManager.hlsRequired) {
				this.videoEl.src = this.hlsSource
			}
		}
		if (
			!mediaManager &&
			this.hlsSource &&
			this.mp4Source &&
			!canPlayNativeHls &&
			!this.usingMp4Source
		) {
			this.videoEl.src = this.mp4Source
			this.usingMp4Source = true
		}
		if (isVideoPlaying(this.videoEl)) return
		if (
			this.hlsSource &&
			mediaManager?.hlsRequired &&
			!this.hlsReady &&
			!this.usingMp4Source
		) {
			await mediaManager.ensureHlsFor(this)
		}
		if (this.isWaitingForHlsLibrary()) return
		if (this.eventDriven && !this.eventEnabled) return
		if (!userRequestedPlayback && !ambientMotionEnabled()) return
		if (this.isHiddenForViewport()) return
		if (percentageSeen(this.videoEl) <= 0) return

		// check for updates that must happen before playing that can be done synchronously
		if (!this.hlsSource && !this.usingMp4Source && this.mp4Source) {
			this.videoEl.src = this.mp4Source
			this.usingMp4Source = true
		}
		// play video w/native html5 video element
		try {
			await this.videoEl.play()
		} catch (error) {
			if (
				error instanceof DOMException &&
				error.name === 'AbortError' &&
				this.videoEl.paused
			)
				return
			throw error
		}
		this.markMediaLoaded()
		this.updateToggleState()
	}

	async resolveNativeHlsSource(): Promise<string> {
		if (!this.hlsSource) return ''
		if (!this.nativeHlsSourcePromise) {
			this.nativeHlsSourcePromise = (async () => {
				const masterUrl = new URL(this.hlsSource!, window.location.href)
				try {
					const response = await fetch(masterUrl)
					if (!response.ok) return masterUrl.href
					const variant = selectHlsVariant(await response.text(), masterUrl, 720)
					return variant ?? masterUrl.href
				} catch {
					return masterUrl.href
				}
			})()
		}
		return this.nativeHlsSourcePromise
	}
	pause() {
		if (isVideoPlaying(this.videoEl)) {
			this.videoEl.pause()
		}
		this.updateToggleState()
	}

	isWaitingForHlsLibrary() {
		if (!this.hlsSource) return false
		if (this.usingMp4Source) return false
		const mediaManager = window?.Ucoast?.mediaManager
		if (!mediaManager) {
			const canPlayNativeHls =
				this.videoEl.canPlayType('application/vnd.apple.mpegurl') !== ''
			return !canPlayNativeHls && !this.mp4Source
		}
		if (!mediaManager.hlsRequired) return false
		if (!this.hlsReady) return true
		return !mediaManager.hlsLibraryLoaded
	}

	scheduleInitRetry() {
		if (this.retryInitTimer !== undefined) return
		if (this.retryAttempts >= 50) return
		this.retryAttempts += 1
		this.retryInitTimer = window.setTimeout(() => {
			this.retryInitTimer = undefined
			this.onConnectedCallback()
			if (!this.eventDriven && !this.autoplayDisabled) {
				this.scheduleAutoplayReady()
			}
		}, 120)
	}

	clearInitRetry() {
		if (this.retryInitTimer === undefined) return
		window.clearTimeout(this.retryInitTimer)
		this.retryInitTimer = undefined
	}

	useMp4Fallback() {
		if (!this.mp4Source || this.usingMp4Source) return
		this.videoEl.src = this.mp4Source
		this.usingMp4Source = true
		this.hlsReady = true
		window?.Ucoast?.mediaManager?.addVideo(this)
	}

	scheduleAutoplayReady() {
		if (this.autoplayReady || this.autoplayReadyTimer !== undefined) return
		if (
			shouldHoldAutoplayForUserIntent(
				this.hasAttribute('data-uc-autoplay-on-mobile')
			)
		) {
			this.bindAutoplayIntent()
			return
		}
		const enableAutoplay = () => {
			if (!this.isConnected) return
			const autoplayDelay =
				this.hasAttribute('data-uc-autoplay-on-mobile') &&
				window.matchMedia('(max-width: 767px)').matches
					? 0
					: 1200
			this.autoplayReadyTimer = window.setTimeout(() => {
				this.autoplayReadyTimer = undefined
				if (!this.isConnected) return
				this.autoplayReady = true
				void this.play()
			}, autoplayDelay)
		}

		if (document.readyState === 'complete') {
			enableAutoplay()
			return
		}
		window.addEventListener('load', enableAutoplay, { once: true })
	}

	clearAutoplayReady() {
		if (this.autoplayReadyTimer === undefined) return
		window.clearTimeout(this.autoplayReadyTimer)
		this.autoplayReadyTimer = undefined
	}

	bindAutoplayIntent() {
		if (this.autoplayIntentBound) return
		const options: AddEventListenerOptions = { once: true, passive: true }
		window.addEventListener(
			'pointerdown',
			this.onAutoplayIntentBound,
			options
		)
		window.addEventListener('keydown', this.onAutoplayIntentBound, {
			once: true,
		})
		window.addEventListener('scroll', this.onAutoplayIntentBound, options)
		this.autoplayIntentBound = true
	}

	unbindAutoplayIntent() {
		if (!this.autoplayIntentBound) return
		window.removeEventListener('pointerdown', this.onAutoplayIntentBound)
		window.removeEventListener('keydown', this.onAutoplayIntentBound)
		window.removeEventListener('scroll', this.onAutoplayIntentBound)
		this.autoplayIntentBound = false
	}

	onAutoplayIntent() {
		this.unbindAutoplayIntent()
		this.autoplayReady = true
		void this.play()
	}

	async onLibraryLoad() {
		// don't run if this isn't an hls video or if hls is already initalized for this element
		if (!this.hlsSource) return
		if (this.hlsInstance) return
		const mediaManager = window?.Ucoast?.mediaManager
		if (!mediaManager?.Hls) return
		if (!mediaManager.hlsLibIsSupported) {
			this.useMp4Fallback()
			return
		}
		// initialize hls - this is a 3ms process that doesn't load any video data until a video is played
		this.hlsInstance = new mediaManager.Hls.default({
			videoPreference: {
				preferHDR: true,
			},
			capLevelToPlayerSize: !this.limitHlsOnMobile,
			ignoreDevicePixelRatio: true,
			backBufferLength: 2,
			maxBufferLength: 6,
			maxMaxBufferLength: 8,
		})
		this.hlsInstance.on('hlsManifestParsed', () => {
			if (this.limitHlsOnMobile) {
				const mobileLevelCap = this.hlsInstance.levels.reduce(
					(cap: number, level: { height: number }, index: number) =>
						level.height <= 720 ? index : cap,
					0
				)
				this.hlsInstance.autoLevelCapping = mobileLevelCap
			}
			this.hlsReady = true
			void this.play()
		})
		await this.hlsInstance.loadSource(this.hlsSource)
		await this.hlsInstance.attachMedia(this.videoEl)
		// all videos present in DOM at page load are added to the mediaManager.videos array, but if this video was added to the DOM later, it needs to be added to the array

		mediaManager.addVideo(this)
	}

	isHiddenForViewport() {
		const rect = this.videoEl.getBoundingClientRect()
		return rect.height < 1 || rect.width < 1
	}

	bindControls() {
		if (this.controlsBound) return
		if (!this.toggleButton && !this.muteToggleButton) return
		if (this.toggleButton) {
			this.toggleButton.addEventListener('click', this.onToggleClickBound)
		}
		if (this.muteToggleButton) {
			this.muteToggleButton.addEventListener(
				'click',
				this.onMuteToggleClickBound
			)
			this.videoEl.addEventListener(
				'volumechange',
				this.onVideoVolumeChangedBound
			)
		}
		this.videoEl.addEventListener('play', this.onVideoStateChangedBound)
		this.videoEl.addEventListener('pause', this.onVideoStateChangedBound)
		this.videoEl.addEventListener('ended', this.onVideoStateChangedBound)
		this.controlsBound = true
		this.updateToggleState()
		this.updateMuteToggleState()
	}

	unbindControls() {
		if (!this.controlsBound) return
		if (this.toggleButton) {
			this.toggleButton.removeEventListener(
				'click',
				this.onToggleClickBound
			)
		}
		if (this.muteToggleButton) {
			this.muteToggleButton.removeEventListener(
				'click',
				this.onMuteToggleClickBound
			)
			this.videoEl.removeEventListener(
				'volumechange',
				this.onVideoVolumeChangedBound
			)
		}
		this.videoEl.removeEventListener('play', this.onVideoStateChangedBound)
		this.videoEl.removeEventListener('pause', this.onVideoStateChangedBound)
		this.videoEl.removeEventListener('ended', this.onVideoStateChangedBound)
		this.controlsBound = false
	}

	onToggleClick(event: Event) {
		event.preventDefault()
		if (isVideoPlaying(this.videoEl)) {
			this.userPaused = true
			this.userPlayRequested = false
			this.pause()
			return
		}
		this.userPaused = false
		this.userPlayRequested = true
		this.eventEnabled = true
		this.autoplayReady = true
		void this.play({ userInitiated: true })
	}

	onMuteToggleClick(event: Event) {
		event.preventDefault()
		this.videoEl.muted = !this.videoEl.muted
	}

	onVideoStateChanged() {
		if (isVideoPlaying(this.videoEl)) {
			this.markMediaLoaded()
		}
		this.updateToggleState()
	}

	markMediaLoaded() {
		this.setAttribute('data-uc-has-played', 'true')
		const wrapper =
			this.wrapper ??
			q.oc<ArtDirection & HTMLElement>(this, 'art-direction')
		if (!wrapper) return
		this.wrapper = wrapper
		wrapper.onMediaLoad()
	}

	updateToggleState() {
		if (!this.toggleButton) return
		const isPaused = !isVideoPlaying(this.videoEl)
		const label = isPaused
			? this.toggleButton.dataset.labelPlay ?? 'Play video'
			: this.toggleButton.dataset.labelPause ?? 'Pause video'
		this.toggleButton.setAttribute(
			'data-state',
			isPaused ? 'paused' : 'playing'
		)
		this.toggleButton.setAttribute('aria-label', label)
		if (this.toggleLabel) {
			this.toggleLabel.textContent = label
		}
	}

	updateMuteToggleState() {
		if (!this.muteToggleButton) return
		const isMuted = this.videoEl.muted
		const label = isMuted
			? this.muteToggleButton.dataset.labelUnmute ?? 'Unmute video'
			: this.muteToggleButton.dataset.labelMute ?? 'Mute video'
		this.muteToggleButton.setAttribute(
			'data-state',
			isMuted ? 'muted' : 'unmuted'
		)
		this.muteToggleButton.setAttribute('aria-label', label)
		this.muteToggleButton.setAttribute(
			'aria-pressed',
			isMuted ? 'true' : 'false'
		)
		if (this.muteToggleLabel) {
			this.muteToggleLabel.textContent = label
		}
	}
}
